import CustomHeader from '../../Components/AgGrid/CustomHeader';
import GridCheckBox from '../../Components/Ui/GridCheckBox/GridCheckBox';
import GridCellFileUpload from '../../Components/Ui/GridCellFileUpload/GridCellFileUpload';
import DateTimeCellEditor from '../../Components/AgGrid/DateTimeCellEditor';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Tooltip } from '@mui/material';
import { Button, Checkbox } from '../../Components/MUIs';
import { Button as GridButton } from '../../Components/MUIs';
import dayjs from 'dayjs';

/**
 * AG-Grid 컬럼 정의를 생성하는 범용 함수
 * CreateColumn('필드명', '컬럼이름', '정렬'(기본값 'left'), 너비(기본값 120), 편집여부(기본값 true), 필수여부(기본값 false), 숨김여부(기본값 false), 데이터타입(기본값 'text'), 기타필요데이터 또는 스타일)
 * 데이터타입: 'button' (기본 버튼) | 'requiredButton' (필수 버튼) | 'checkbox' | 'radio' | 'text' | 'number' | 'date' | 'datetime' (날짜+시간) | 'select' | 'phone' | 'businessNumber' | 'upload' | 'download' | 'fileButton' (파일 버튼)
 * GridFilterButton: filterable(true/false), filterValueGetter, filterOptions — 컬럼별 필터 포함·제외 및 필터 비교값
 */
export const CreateColumn = (
  field = '',
  headerName = '',
  align = 'left',
  width = 120,
  editable = true,
  required = false,
  hide = false,
  dataType = 'text',
  extraData = {}
) => {
  const {
    // === 너비 관련 ===
    minWidth,
    maxWidth,
    flex,

    // === 셀 에디터 관련 ===
    cellEditor,
    cellEditorParams,
    maxLength, // 텍스트 최대 길이

    // === 셀 렌더러 관련 ===
    cellRenderer,

    // === 값 처리 관련 ===
    valueGetter,
    valueSetter,
    valueFormatter,
    valueParser,
    defaultValue,

    // === 스타일 관련 ===
    cellClassRules,
    cellClass,
    customCellStyle,

    // === 날짜 관련 ===
    dateFormat = 'YYYY-MM-DD',

    // === 셀렉트 박스 관련 ===
    selectOptions = [],
    selectValueKey = 'value',
    selectLabelKey = 'label',

    // === 라디오 버튼 관련 ===
    radioOptions = [],
    radioValueKey = 'value',
    radioLabelKey = 'label',

    // === 편집불가 컬럼 관련 ===
    tooltip, // 툴팁 텍스트 (편집불가 컬럼용)

    // === 필수 버튼 컬럼 관련 (requiredButton 타입용) ===
    buttonRenderer, // 버튼 렌더러 함수 (커스텀 렌더러)
    buttonClickHandler, // 버튼 클릭 핸들러 함수 (params) => void
    buttonEmptyTooltip, // 값이 없을 때 툴팁 텍스트
    buttonValueTooltip, // 값이 있을 때 툴팁 텍스트
    buttonEmptyText = '설정하기', // 값이 없을 때 버튼 텍스트
    buttonValueStyle, // 값이 있을 때 버튼 스타일 (기본: 녹색, 밑줄, 클릭 가능)

    // === 기본 버튼 컬럼 관련 (button 타입용) ===
    buttonText = '이동', // 버튼 텍스트
    buttonCondition, // 버튼 표시 조건 함수 (params) => boolean, true면 표시, false면 null 반환
    buttonSize = 'small', // 버튼 크기
    buttonSx, // 버튼 스타일

    // === 파일 업로드 컬럼 관련 ===
    uploadRenderer, // 파일 업로드 렌더러 함수 (커스텀 렌더러)
    uploadProps, // GridCellFileUpload에 전달할 props (gridRef, props 등)

    // === 다운로드 컬럼 관련 ===
    downloadRenderer, // 다운로드 렌더러 함수 (커스텀 렌더러)
    downloadClickHandler, // 다운로드 클릭 핸들러 함수 (data) => void
    downloadButtonText = '다운로드', // 다운로드 버튼 텍스트

    // === 파일 버튼 컬럼 관련 (fileButton 타입용) ===
    fileButtonClickHandler, // 파일 버튼 클릭 핸들러 함수 (data, fieldName) => void
    fileButtonFileNameKey = 'fileName', // 파일명 키 (기본값: 'fileName', 대문자 버전: 'FILE_NAME')
    fileButtonFilePathKey = 'filePath', // 파일 경로 키 (기본값: 'filePath', 대문자 버전: 'FILE_PATH')
    fileButtonRenderer, // 파일 버튼 렌더러 함수 (커스텀 렌더러)
    fileButtonEmptyText = '업로드', // 사진/파일이 없을 때 버튼 텍스트
    fileButtonValueText, // 사진/파일이 있을 때 버튼 텍스트 (지정 시 사용, 미지정 시 fileName || '업로드'). 문자열 또는 (params) => string

    // === 체크박스 컬럼 관련 (NO 컬럼용) ===
    checkboxProps, // GridCheckBox에 전달할 props

    // === 조건부 편집 관련 ===
    editableCondition,

    // === GridFilterButton 컬럼별 필터 ===
    filterable, // true: 필터 포함, false: 제외 (미지정 시 cellRenderer 규칙)
    filterValueGetter, // 필터 옵션·비교값 (미지정 시 row[field])
    filterOptions, // 고정 선택지 (예: ['내부인', '외부인'])

    // === 기타 AG-Grid 속성 ===
    pinned,
    rowGroup = false,
    enableRowGroup = false,
    mobileData,
    headerComponentParams,
    ...rest
  } = extraData;

  // 기본 셀 스타일 생성
  const getCellStyle = () => {
    if (customCellStyle) {
      return typeof customCellStyle === 'function'
        ? customCellStyle
        : customCellStyle;
    }

    const baseStyle = {
      display: 'flex',
      alignItems: 'center',
    };

    // 정렬 설정
    if (align === 'center') {
      baseStyle.justifyContent = 'center';
    } else if (align === 'right') {
      baseStyle.justifyContent = 'flex-end';
      baseStyle.textAlign = 'right';
    } else {
      baseStyle.justifyContent = 'flex-start';
    }

    return baseStyle;
  };

  // 기본 컬럼 정의
  const columnDef = {
    field,
    headerName,
    editable: editableCondition || editable,
    hide,
    cellStyle: getCellStyle(),
    ...rest,
  };

  // 너비 설정
  columnDef.width = width;
  if (minWidth) columnDef.minWidth = minWidth;
  if (maxWidth) columnDef.maxWidth = maxWidth;
  if (flex) {
    columnDef.flex = flex;
    // flex가 있으면 width는 무시됨
    delete columnDef.width;
  }

  // 헤더 설정 (필수 컬럼인 경우 붉은색 * 표시)
  if (required) {
    columnDef.headerComponent = CustomHeader;
    columnDef.headerComponentParams = {
      required: true,
      ...headerComponentParams,
    };
  } else if (headerComponentParams) {
    columnDef.headerComponentParams = headerComponentParams;
  }

  // === 데이터 타입별 처리 ===
  switch (dataType) {
    case 'button':
      // 기본 버튼 컬럼
      columnDef.editable = false;
      columnDef.cellStyle = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      };
      if (buttonRenderer) {
        columnDef.cellRenderer = buttonRenderer;
      } else if (buttonClickHandler) {
        columnDef.cellRenderer = (params) => {
          // 조건이 있고 false면 null 반환
          if (buttonCondition && !buttonCondition(params)) {
            return null;
          }

          return (
            <Button
              size={buttonSize}
              name={buttonText}
              sx={buttonSx || { width: '100%' }}
              onClick={() => buttonClickHandler(params)}
            />
          );
        };
      } else if (cellRenderer) {
        columnDef.cellRenderer = cellRenderer;
      }
      break;

    case 'requiredButton':
      columnDef.editable = false;
      columnDef.cellStyle = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      };

      if (buttonRenderer) {
        columnDef.cellRenderer = buttonRenderer;
      } else if (buttonClickHandler) {
        const tooltipSx = {
          fontSize: '0.95rem',
          padding: '10px 16px',
          maxWidth: 'none',
          backgroundColor: '#333',
        };

        const getTooltipProps = (title) =>
          title
            ? {
              title,
              arrow: true,
              enterDelay: 0,
              componentsProps: { tooltip: { sx: tooltipSx } },
            }
            : null;

        columnDef.cellRenderer = (params) => {
          if (!params.data) return <span></span>;

          const value = params.data[field];
          // extraData에서 showEmptyButton 옵션을 가져옴 (기본값: true)
          const showEmptyButton = extraData.showEmptyButton ?? true;

          const defaultButtonStyle = {
            color: 'green',
            fontSize: '0.9rem',
            fontWeight: 'bold',
            cursor: 'pointer',
            textDecoration: 'underline',
            transition: 'color 0.2s ease',
            background: 'none',
            border: 'none',
            padding: 0,
          };

          if (value) {
            const justifyContent =
              align === 'right'
                ? 'flex-end'
                : align === 'left'
                  ? 'flex-start'
                  : 'center';

            const button = (
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent,
                }}
              >
                <button
                  type="button"
                  onClick={() => buttonClickHandler(params)}
                  style={buttonValueStyle || defaultButtonStyle}
                  onMouseEnter={(e) => {
                    if (!buttonValueStyle)
                      e.currentTarget.style.color = '#1565c0';
                  }}
                  onMouseLeave={(e) => {
                    if (!buttonValueStyle)
                      e.currentTarget.style.color = 'green';
                  }}
                >
                  {value}
                </button>
              </div>
            );

            const tooltipText =
              typeof buttonValueTooltip === 'function'
                ? buttonValueTooltip(params)
                : buttonValueTooltip;
            const tooltipProps = getTooltipProps(tooltipText);

            return tooltipProps ? (
              <Tooltip {...tooltipProps}>{button}</Tooltip>
            ) : (
              button
            );
          } else {
            if (showEmptyButton) {
              const emptyButton = (
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Button
                    size="small"
                    name={buttonEmptyText}
                    sx={{ width: '100%' }}
                    onClick={() => buttonClickHandler(params)}
                  />
                </div>
              );

              const tooltipProps = getTooltipProps(buttonEmptyTooltip);
              return tooltipProps ? (
                <Tooltip {...tooltipProps}>
                  <span style={{ display: 'inline-flex', width: '100%' }}>
                    {emptyButton}
                  </span>
                </Tooltip>
              ) : (
                emptyButton
              );
            }

            return <span></span>;
          }
        };
      } else if (cellRenderer) {
        columnDef.cellRenderer = cellRenderer;
      }
      break;

    case 'checkbox':
      // 체크박스 컬럼
      if (checkboxProps) {
        // NO 컬럼용 특별한 체크박스
        const { maxWidth: cbMaxWidth, ...cbRest } = extraData;
        const cbWidth = cbMaxWidth || width;
        return {
          headerCheckboxSelection: checkboxProps.headerCheckboxSelection !== false,
          // 필터(외부 필터 포함)로 숨겨진 행은 헤더 전체선택 대상에서 제외
          headerCheckboxSelectionFilteredOnly:
            checkboxProps.headerCheckboxSelectionFilteredOnly !== false,
          field,
          headerName,
          ...cbRest,
          cellStyle: {
            textAlign: 'right',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 0,
          },
          editable: false,
          cellClass: 'delete-action-cell no-cell-padding',
          cellRenderer: (params) => {
            return (
              <GridCheckBox
                params={params}
                DeleteNewRow={
                  checkboxProps.DeleteNewRow1 || checkboxProps.DeleteNewRow
                }
                RestoreRow={
                  checkboxProps.RestoreRow1 || checkboxProps.RestoreRow
                }
                gridRef={checkboxProps.gridRef1 || checkboxProps.gridRef}
                rowData={checkboxProps.rowData1 || checkboxProps.rowData}
                selectedRowNosRef={
                  checkboxProps.selectedRowNosRef1 ||
                  checkboxProps.selectedRowNosRef
                }
                lastCheckedIndexRef={
                  checkboxProps.lastCheckedIndexRef1 ||
                  checkboxProps.lastCheckedIndexRef
                }
                handleRowSelection={checkboxProps.handleRowSelection}
                dispatchGrid={checkboxProps.dispatchGrid}
                gridState={checkboxProps.gridState}
                preferNodeRowData={checkboxProps.preferNodeRowData}
                hideCheckboxCondition={checkboxProps.hideCheckboxCondition}
                enableDragSelection={checkboxProps.enableDragSelection}
              />
            );
          },
          width: cbWidth,
          minWidth: cbWidth,
          maxWidth: cbMaxWidth || cbWidth,
          // defaultColDef.flex(예: 1) 상속 시 체크/선택마다 가변폭 컬럼들이 재분배되며 흔들리는 현상 방지
          flex: 0,
          resizable: false,
        };
      } else {
        // 기본 체크박스
        // field가 있고 editable이 true인 경우: 데이터 필드 값(Y/N) 기반 체크박스
        // 그 외의 경우: ag-grid 기본 행 선택 체크박스
        if (field && editable) {
          // 데이터 필드 값 기반 체크박스 (Y/N 값 관리)
          columnDef.editable = true;
          columnDef.cellStyle = {
            ...getCellStyle(),
            justifyContent: 'center',
          };
          columnDef.cellRenderer = (params) => {
            return (
              <Checkbox
                name=""
                checked={params.value === 'Y'}
                onChange={(change) => {
                  params.setValue(change ? 'Y' : 'N');
                }}
                nameSx={{ m: 0 }}
              />
            );
          };
        } else {
          // ag-grid 기본 행 선택 체크박스
          columnDef.checkboxSelection = true;
          columnDef.editable = false;
          columnDef.cellStyle = {
            ...getCellStyle(),
            justifyContent: 'center',
          };
        }
      }
      break;

    case 'radio':
      // 라디오 버튼 컬럼
      columnDef.editable = false;
      if (radioOptions.length > 0) {
        columnDef.cellRenderer =
          cellRenderer ||
          ((params) => {
            const value = params.value;
            return (
              <div
                style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
              >
                {radioOptions.map((option) => {
                  const optionValue =
                    typeof option === 'string' ? option : option[radioValueKey];
                  const optionLabel =
                    typeof option === 'string'
                      ? option
                      : option[radioLabelKey] || option.name;
                  const isChecked = value === optionValue;
                  return (
                    <label
                      key={optionValue}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        name={`${field}_${params.rowIndex}`}
                        value={optionValue}
                        checked={isChecked}
                        onChange={(e) => {
                          params.data[field] = e.target.value;
                          params.api.refreshCells({
                            rowNodes: [params.node],
                            columns: [field],
                          });
                        }}
                      />
                      <span>{optionLabel}</span>
                    </label>
                  );
                })}
              </div>
            );
          });
      }
      break;

    case 'date':
      // 날짜 컬럼
      columnDef.cellEditor = cellEditor || 'agDateStringCellEditor';
      columnDef.valueFormatter =
        valueFormatter ||
        ((params) => {
          if (!params.value) return '';
          return dayjs(params.value).format(dateFormat);
        });
      columnDef.valueParser =
        valueParser ||
        ((params) => {
          if (!params.newValue) return null;
          const [year, month, day] = params.newValue.split('-');
          return new Date(Date.UTC(year, month - 1, day)).toISOString();
        });
      columnDef.cellStyle = {
        ...getCellStyle(),
        justifyContent: 'center',
      };
      break;

    case 'datetime':
      // 날짜+시간 컬럼 (시간대별 통계 등)
      const datetimeFormat = dateFormat || 'YYYY-MM-DD HH:mm:ss';
      columnDef.cellEditor = cellEditor || DateTimeCellEditor;
      columnDef.cellEditorParams = cellEditorParams || { format: datetimeFormat };
      columnDef.valueFormatter =
        valueFormatter ||
        ((params) => {
          if (!params.value) return '';
          return dayjs(params.value).isValid()
            ? dayjs(params.value).format(datetimeFormat)
            : String(params.value ?? '');
        });
      columnDef.valueParser =
        valueParser ||
        ((params) => {
          if (!params.newValue) return null;
          const v = params.newValue;
          return dayjs(v).isValid() ? dayjs(v).format(datetimeFormat) : v;
        });
      columnDef.cellStyle = {
        ...getCellStyle(),
        justifyContent: 'center',
      };
      break;

    case 'select':
      // 셀렉트 박스 컬럼
      columnDef.cellEditor = cellEditor || 'agSelectCellEditor';
      if (selectOptions.length > 0) {
        const options = selectOptions.map((item) => {
          if (typeof item === 'string') return item;
          return item[selectLabelKey] || item.name || item;
        });
        columnDef.cellEditorParams = cellEditorParams || { values: options };
      }
      if (defaultValue) {
        columnDef.valueGetter =
          valueGetter ||
          ((params) => {
            return params.data[field] || defaultValue;
          });
        columnDef.valueSetter =
          valueSetter ||
          ((params) => {
            params.data[field] = params.newValue;
            // 옵션이 객체인 경우 value 키도 설정
            if (
              selectOptions.length > 0 &&
              typeof selectOptions[0] === 'object'
            ) {
              const selected = selectOptions.find(
                (item) =>
                  (item[selectLabelKey] || item.name) === params.newValue
              );
              if (selected && selected[selectValueKey]) {
                // value 키가 다른 필드명인 경우 (예: AUTH_CD)
                const valueField = field
                  .replace(/_NM$/, '_CD')
                  .replace(/NAME$/, 'CODE');
                if (valueField !== field) {
                  params.data[valueField] = selected[selectValueKey];
                }
              }
            }
            return true;
          });
      }
      break;

    case 'phone':
      // 전화번호 컬럼 (포맷: 010-0000-0000)
      columnDef.cellEditor = cellEditor || 'agTextCellEditor';

      columnDef.cellEditorParams = cellEditorParams || {
        maxLength: 13,
      };

      columnDef.valueParser = (params) => {
        if (!params.newValue) return null;
        return params.newValue.replace(/[^0-9]/g, '').slice(0, 11);
      };

      columnDef.valueFormatter = (params) => {
        if (!params.value) return '';
        const phone = params.value;

        return phone.length === 11
          ? `${phone.slice(0, 3)}-${phone.slice(3, 7)}-${phone.slice(7)}`
          : phone;
      };
      break;

    case 'businessNumber':
      // 사업자번호 컬럼 (포맷: 123-41-23411)
      columnDef.cellEditor = cellEditor || 'agTextCellEditor';
      columnDef.cellEditorParams = cellEditorParams || {
        maxLength: 12,
      };
      columnDef.valueFormatter =
        valueFormatter ||
        ((params) => {
          if (!params.value) return '';
          const business = params.value.replace(/[^0-9]/g, '');
          if (business.length === 10) {
            return `${business.slice(0, 3)}-${business.slice(3, 5)}-${business.slice(5)}`;
          }
          return params.value;
        });
      columnDef.valueParser =
        valueParser ||
        ((params) => {
          if (!params.newValue) return null;
          return params.newValue.replace(/[^0-9]/g, '');
        });
      break;

    case 'number':
      // 숫자 컬럼 (정수)
      columnDef.cellEditor = cellEditor || 'agNumberCellEditor';
      columnDef.cellStyle = {
        ...getCellStyle(),
        justifyContent: 'right',
        textAlign: 'center',
      };
      // 기본 valueParser: 숫자만 추출하고 파싱
      if (!valueParser) {
        columnDef.valueParser = (params) => {
          if (
            params.newValue === null ||
            params.newValue === undefined ||
            params.newValue === ''
          ) {
            return null;
          }
          // 숫자만 추출
          const numeric = params.newValue.toString().replace(/[^0-9]/g, '');
          if (!numeric) return null;
          const parsed = parseInt(numeric, 10);
          return isNaN(parsed) ? null : parsed;
        };
      }
      // 기본 valueFormatter: 숫자 값을 표시
      if (!valueFormatter) {
        columnDef.valueFormatter = (params) => {
          if (params.value === null || params.value === undefined) return '';
          return params.value.toString();
        };
      }
      break;

    case 'upload':
      // 파일 업로드 컬럼
      columnDef.editable = false;
      columnDef.cellStyle = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      };
      if (uploadRenderer) {
        columnDef.cellRenderer = uploadRenderer;
      } else if (uploadProps) {
        columnDef.cellRenderer = (e) => {
          return (
            <GridCellFileUpload
              label={uploadProps.label || '업로드'}
              extension={uploadProps.extension || ''}
              gridRef={uploadProps.gridRef}
              e={e}
              props={uploadProps.props}
            />
          );
        };
      } else if (cellRenderer) {
        columnDef.cellRenderer = cellRenderer;
      }
      break;

    case 'download':
      // 다운로드 컬럼
      columnDef.editable = false;
      columnDef.cellStyle = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      };
      if (downloadRenderer) {
        columnDef.cellRenderer = downloadRenderer;
      } else if (downloadClickHandler) {
        columnDef.cellRenderer = (e) => {
          return (
            <Button
              size="small"
              name={downloadButtonText}
              sx={{ width: '100%' }}
              onClick={() => {
                downloadClickHandler(e.data);
              }}
            />
          );
        };
      } else if (cellRenderer) {
        columnDef.cellRenderer = cellRenderer;
      }
      break;

    case 'fileButton':
      // 파일 버튼 컬럼
      columnDef.editable = false;
      columnDef.cellStyle = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '4px 8px',
      };
      if (fileButtonRenderer) {
        columnDef.cellRenderer = fileButtonRenderer;
      } else if (fileButtonClickHandler) {
        columnDef.cellRenderer = (params) => {
          // 파일명 키 결정 (대소문자 모두 지원)
          const fileNameKey =
            fileButtonFileNameKey === 'fileName'
              ? params.value &&
                typeof params.value === 'object' &&
                'FILE_NAME' in params.value
                ? 'FILE_NAME'
                : 'fileName'
              : fileButtonFileNameKey;
          const filePathKey =
            fileButtonFilePathKey === 'filePath'
              ? params.value &&
                typeof params.value === 'object' &&
                'FILE_PATH' in params.value
                ? 'FILE_PATH'
                : 'filePath'
              : fileButtonFilePathKey;

          const hasFile =
            params.value &&
            ((typeof params.value === 'string' && params.value.trim() !== '') ||
              (typeof params.value === 'object' &&
                (params.value[fileNameKey] ||
                  params.value.FILE_NAME ||
                  params.value.fileName)));
          const fileName =
            typeof params.value === 'string'
              ? params.value
              : params.value?.[fileNameKey] ||
              params.value?.FILE_NAME ||
              params.value?.fileName ||
              '';
          const filePath =
            typeof params.value === 'string'
              ? params.value
              : params.value?.[filePathKey] ||
              params.value?.FILE_PATH ||
              params.value?.filePath ||
              '';

          const buttonText = hasFile
            ? (typeof fileButtonValueText === 'function'
                ? fileButtonValueText(params)
                : fileButtonValueText !== undefined && fileButtonValueText !== null
                  ? fileButtonValueText
                  : fileName || '업로드')
            : fileButtonEmptyText;
          return (
            <Tooltip
              title={hasFile ? filePath || fileName : fileButtonEmptyText}
              arrow
              placement="top"
            >
              <span
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <GridButton
                  size="small"
                  name={buttonText}
                  variant={hasFile ? 'outlined' : 'contained'}
                  sx={{
                    width: '100%',
                    fontSize: '0.8rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minWidth: 0,
                  }}
                  onClick={() => {
                    fileButtonClickHandler(params.data, field);
                  }}
                />
              </span>
            </Tooltip>
          );
        };
        // 필수 검증을 위한 cellClassRules
        if (required) {
          columnDef.cellClassRules = cellClassRules || {
            'required-cell': (params) => {
              const value = params.value;
              if (!value) return true;
              if (typeof value === 'string') return value.trim() === '';
              if (typeof value === 'object') {
                // FILE_NAME, fileName 모두 체크
                const fileName =
                  value.FILE_NAME ||
                  value.fileName ||
                  value[fileButtonFileNameKey];
                return !fileName || fileName.trim() === '';
              }
              return true;
            },
          };
        } else if (cellClassRules) {
          columnDef.cellClassRules = cellClassRules;
        }
      } else if (cellRenderer) {
        columnDef.cellRenderer = cellRenderer;
      }
      break;

    case 'text':
    default:
      // 텍스트 컬럼
      if (maxLength) {
        columnDef.cellEditor = cellEditor || 'agTextCellEditor';
        columnDef.cellEditorParams = cellEditorParams || {
          maxLength,
          parseValue: (value) => String(value || ''),
        };
        columnDef.valueFormatter =
          valueFormatter ||
          ((params) => {
            const val = params.value;
            return val !== undefined ? String(val) : '';
          });
      }
      break;
  }

  // 셀 에디터 설정 (데이터 타입별 기본값이 아닌 경우)
  if (cellEditor && dataType !== 'date' && dataType !== 'select') {
    columnDef.cellEditor = cellEditor;
    if (cellEditorParams) columnDef.cellEditorParams = cellEditorParams;
  }

  // 셀 렌더러 설정 (데이터 타입이 'button', 'requiredButton', 'fileButton'이 아니고 cellRenderer가 제공된 경우)
  if (
    cellRenderer &&
    dataType !== 'button' &&
    dataType !== 'requiredButton' &&
    dataType !== 'fileButton' &&
    dataType !== 'radio'
  ) {
    columnDef.cellRenderer = cellRenderer;
  }

  // 편집불가 컬럼에 툴팁 추가
  if (!editable && tooltip) {
    const originalRenderer = columnDef.cellRenderer;
    const originalValueFormatter = columnDef.valueFormatter;
    columnDef.cellRenderer = (params) => {
      let content;
      if (originalRenderer) {
        content = originalRenderer(params);
      } else if (originalValueFormatter) {
        // valueFormatter가 있으면 포맷팅된 값 사용 (date, phone, businessNumber 등)
        content = originalValueFormatter(params);
      } else {
        content = params.value || '';
      }

      // tooltip이 함수인 경우 호출하여 값을 가져옴
      const tooltipText =
        typeof tooltip === 'function' ? tooltip(params) : tooltip;

      // 컬럼 정렬에 맞게 justifyContent 설정
      const justifyContent =
        align === 'center'
          ? 'center'
          : align === 'right'
            ? 'flex-end'
            : 'flex-start';

      return (
        <Tooltip
          title={tooltipText || ''}
          arrow
          enterDelay={300}
          componentsProps={{
            tooltip: {
              sx: {
                fontSize: '0.9rem',
                backgroundColor: '#444',
                color: '#fff',
                padding: '8px 12px',
              },
            },
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent,
              gap: '4px',
              width: '100%',
              height: '100%',
            }}
          >
            <InfoOutlinedIcon fontSize="small" sx={{ color: '#888' }} />
            <span>{content}</span>
          </div>
        </Tooltip>
      );
    };
  }

  // 값 처리 설정
  if (valueGetter) columnDef.valueGetter = valueGetter;
  if (valueSetter) columnDef.valueSetter = valueSetter;
  if (
    valueFormatter &&
    dataType !== 'date' &&
    dataType !== 'phone' &&
    dataType !== 'businessNumber'
  )
    columnDef.valueFormatter = valueFormatter;
  if (
    valueParser &&
    dataType !== 'date' &&
    dataType !== 'phone' &&
    dataType !== 'businessNumber'
  )
    columnDef.valueParser = valueParser;

  // 필수 입력 검증 (미입력시 붉은색 처리) - fileButton 타입은 이미 처리됨
  if (required && dataType !== 'fileButton') {
    columnDef.cellClassRules = cellClassRules || {
      'required-cell': (params) => {
        const value = params.value;
        if (typeof value === 'string') return !value || value.trim() === '';
        if (typeof value === 'number')
          return value === null || value === undefined;
        return !value;
      },
    };
  } else if (cellClassRules && dataType !== 'fileButton') {
    columnDef.cellClassRules = cellClassRules;
  }

  // 기타 설정
  if (cellClass) columnDef.cellClass = cellClass;
  if (pinned) columnDef.pinned = pinned;
  if (rowGroup) columnDef.rowGroup = true;
  if (enableRowGroup) columnDef.enableRowGroup = true;
  if (mobileData) columnDef.mobileData = mobileData;
  if (filterable !== undefined) columnDef.filterable = filterable;
  if (typeof filterValueGetter === 'function') {
    columnDef.filterValueGetter = filterValueGetter;
  }
  if (Array.isArray(filterOptions)) columnDef.filterOptions = filterOptions;

  return columnDef;
};

export default CreateColumn;
